import React, { useState, useEffect } from 'react';
import { Bar, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import DatePicker from 'react-datepicker';
import Skeleton from 'react-loading-skeleton';

// Leaflet imports + critical icon fix
import 'leaflet/dist/leaflet.css';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';

// Fix default Leaflet marker icons for Create React App / Webpack 5+
delete L.Icon.Default.prototype._getIconUrl;

L.Icon.Default.mergeOptions({
  iconRetinaUrl: require('leaflet/dist/images/marker-icon-2x.png').default,
  iconUrl: require('leaflet/dist/images/marker-icon.png').default,
  shadowUrl: require('leaflet/dist/images/marker-shadow.png').default,
});

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const LiveWorkforce = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [filterSite, setFilterSite] = useState('');
  const [filterEmployee, setFilterEmployee] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [employees, setEmployees] = useState([]);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = () => {
    setIsLoading(true);
    // Simulate API call
    setTimeout(() => {
      const sampleEmployees = [
        {
          id: 1,
          name: 'John Doe',
          site: 'Site A',
          status: 'On-site',
          lastPing: '5 min ago',
          lat: 51.505,
          lng: -0.09,
        },
        {
          id: 2,
          name: 'Jane Smith',
          site: 'Site B',
          status: 'Moving',
          lastPing: '2 min ago',
          lat: 51.51,
          lng: -0.1,
        },
        {
          id: 3,
          name: 'Bob Johnson',
          site: 'Site A',
          status: 'Idle',
          lastPing: '10 min ago',
          lat: 51.5,
          lng: -0.08,
        },
        {
          id: 4,
          name: 'Alice Brown',
          site: 'Site C',
          status: 'On-site',
          lastPing: '1 min ago',
          lat: 51.52,
          lng: -0.11,
        },
      ];
      setEmployees(sampleEmployees);
      setIsLoading(false);
    }, 2000);
  };

  const handleRefresh = () => {
    setRefreshing(true);
    setTimeout(() => {
      loadData();
      setRefreshing(false);
    }, 1000);
  };

  // Apply filters
  const filteredEmployees = employees.filter(
    (emp) =>
      (filterSite === '' || emp.site === filterSite) &&
      (filterEmployee === '' ||
        emp.name.toLowerCase().includes(filterEmployee.toLowerCase())) &&
      (filterStatus === '' || emp.status === filterStatus)
  );

  // Chart data calculations
  const statusCounts = filteredEmployees.reduce((acc, emp) => {
    acc[emp.status] = (acc[emp.status] || 0) + 1;
    return acc;
  }, {});

  const pieData = {
    labels: Object.keys(statusCounts),
    datasets: [
      {
        data: Object.values(statusCounts),
        backgroundColor: ['#10B981', '#0EA5E9', '#8B5CF6'], // green, blue, purple
      },
    ],
  };

  const siteCounts = filteredEmployees.reduce((acc, emp) => {
    acc[emp.site] = (acc[emp.site] || 0) + 1;
    return acc;
  }, {});

  const barData = {
    labels: Object.keys(siteCounts),
    datasets: [
      {
        label: 'Workers per Site',
        data: Object.values(siteCounts),
        backgroundColor: '#14B8A6', // teal
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: 'top' },
      tooltip: { enabled: true },
    },
  };

  // Status badge styles
  const statusColors = {
    'On-site': 'bg-green-500 text-white',
    Moving: 'bg-blue-500 text-white',
    Idle: 'bg-purple-500 text-white',
  };

  // Custom colored markers (using external CDN - reliable & no build issues)
  const getMarkerIcon = (status) => {
    let color = 'blue'; // default
    if (status === 'On-site') color = 'green';
    if (status === 'Moving') color = 'blue';
    if (status === 'Idle') color = 'purple';

    return new L.Icon({
      iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
      shadowUrl:
        'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
      iconSize: [25, 41],
      iconAnchor: [12, 41],
      popupAnchor: [1, -34],
      shadowSize: [41, 41],
    });
  };

  return (
    <div className="p-6 bg-white min-h-screen font-roboto text-gray-800">
      {/* Header */}
      <div className="flex justify-between items-center mb-6 flex-wrap gap-4">
        <h1 className="text-3xl font-poppins font-bold">Live Workforce</h1>

        <div className="flex flex-wrap items-center gap-3">
          <DatePicker
            selected={selectedDate}
            onChange={(date) => setSelectedDate(date)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
            dateFormat="MMMM d, yyyy"
          />

          <select
            value={filterSite}
            onChange={(e) => setFilterSite(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]"
          >
            <option value="">All Sites</option>
            <option value="Site A">Site A</option>
            <option value="Site B">Site B</option>
            <option value="Site C">Site C</option>
          </select>

          <input
            type="text"
            value={filterEmployee}
            onChange={(e) => setFilterEmployee(e.target.value)}
            placeholder="Employee name..."
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
          />

          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="border border-gray-300 rounded px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 min-w-[140px]"
          >
            <option value="">All Status</option>
            <option value="On-site">On-site</option>
            <option value="Moving">Moving</option>
            <option value="Idle">Idle</option>
          </select>

          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="bg-blue-600 text-white text-sm px-5 py-1.5 rounded-full hover:bg-blue-700 transition duration-200 shadow-sm flex items-center gap-2 disabled:opacity-70"
          >
            {refreshing && (
              <svg
                className="animate-spin h-4 w-4 text-white"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
            )}
            Refresh
          </button>
        </div>
      </div>

      <hr className="mb-8 border-gray-200" />

      {/* Employee List */}
      <div className="mb-10">
        <h2 className="text-xl font-poppins font-semibold mb-4">
          Live Employee List
        </h2>

        <div className="overflow-x-auto border border-gray-200 rounded-lg">
          {isLoading ? (
            <div className="p-4">
              <Skeleton count={6} height={48} className="mb-2" />
            </div>
          ) : (
            <table className="w-full border-collapse">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-5 text-sm font-semibold text-gray-700">
                    Employee Name
                  </th>
                  <th className="text-left py-3 px-5 text-sm font-semibold text-gray-700">
                    Current Site
                  </th>
                  <th className="text-left py-3 px-5 text-sm font-semibold text-gray-700">
                    Status
                  </th>
                  <th className="text-left py-3 px-5 text-sm font-semibold text-gray-700">
                    Last GPS Ping
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="text-center py-8 text-gray-500">
                      No employees match the current filters
                    </td>
                  </tr>
                ) : (
                  filteredEmployees.map((emp) => (
                    <tr
                      key={emp.id}
                      className="border-t hover:bg-gray-50 transition-colors"
                    >
                      <td className="py-3 px-5">{emp.name}</td>
                      <td className="py-3 px-5">{emp.site}</td>
                      <td className="py-3 px-5">
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-medium ${statusColors[emp.status]} animate-pulse`}
                        >
                          {emp.status}
                        </span>
                      </td>
                      <td className="py-3 px-5">{emp.lastPing}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Map + Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Map */}
        <div>
          <h2 className="text-xl font-poppins font-semibold mb-4">
            Live Locations
          </h2>
          <div className="border border-gray-200 rounded-lg overflow-hidden h-[420px]">
            {isLoading ? (
              <Skeleton height={420} />
            ) : (
              <MapContainer
                center={[51.505, -0.09]}
                zoom={13}
                style={{ height: '100%', width: '100%' }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />
                {filteredEmployees.map((emp) => (
                  <Marker
                    key={emp.id}
                    position={[emp.lat, emp.lng]}
                    icon={getMarkerIcon(emp.status)}
                  >
                    <Popup>
                      <div className="text-sm">
                        <strong>{emp.name}</strong>
                        <br />
                        Site: {emp.site}
                        <br />
                        Status: {emp.status}
                        <br />
                        Last ping: {emp.lastPing}
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            )}
          </div>
        </div>

        {/* Charts */}
        <div className="space-y-8">
          <div>
            <h2 className="text-xl font-poppins font-semibold mb-4">
              Workforce Status Distribution
            </h2>
            <div className="border border-gray-200 rounded-lg p-4 h-[200px]">
              {isLoading ? (
                <Skeleton height={200} />
              ) : (
                <Pie data={pieData} options={chartOptions} />
              )}
            </div>
          </div>

          <div>
            <h2 className="text-xl font-poppins font-semibold mb-4">
              Workers per Site
            </h2>
            <div className="border border-gray-200 rounded-lg p-4 h-[200px]">
              {isLoading ? (
                <Skeleton height={200} />
              ) : (
                <Bar data={barData} options={chartOptions} />
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LiveWorkforce;